import { format, isValid, parse } from "date-fns";

import { buildPatchPayload as buildPaymentPayload } from "../../payments/utils/paymentFormUtils";

import { STRINGS } from "./strings";

export function getContractFileName(fileName) {
  if (!fileName) {
    return "";
  }

  return typeof fileName === "string" ? fileName : (fileName.name ?? fileName.fileName ?? "");
}

export function getContractFileUrl(value) {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  return value.url ?? value.file ?? "";
}

export function getContractFileInitValue(contractData) {
  const fileName = getContractFileName(
    contractData?.contractFileName ?? contractData?.contract_file_name
  );

  return fileName ? { name: fileName } : null;
}

export function parseDateValue(value) {
  if (!value) {
    return null;
  }

  const parsed = parse(value, "yyyy-MM-dd", new Date());
  return isValid(parsed) ? parsed : null;
}

export function formatDateValue(value) {
  if (!value || !isValid(value)) {
    return null;
  }

  return format(value, "yyyy-MM-dd");
}

export function mapContractToFormState(contractData) {
  const safeContractData = contractData ?? {};

  return {
    contractName: safeContractData.contractName ?? "",
    startDate: parseDateValue(safeContractData.startDate),
    termDate: parseDateValue(safeContractData.termDate),
    employerIdentificationNumber: safeContractData.employerIdentificationNumber ?? "",
    pipState: typeof safeContractData.pipState === "boolean" ? safeContractData.pipState : null,
    reviewContract: false,
    clientManagementSplit: "",
    providerSplit: "",
    marketingSplit: "",
    contractFile: getContractFileInitValue(safeContractData),
  };
}

export function buildPatchPayload(formValues) {
  return {
    contractName: formValues.contractName?.trim() ?? "",
    startDate: formatDateValue(formValues.startDate),
    termDate: formatDateValue(formValues.termDate),
    employerIdentificationNumber: formValues.employerIdentificationNumber?.trim() ?? "",
    pipState: formValues.pipState,
  };
}

export function buildContractPaymentPayload(formValues, entityId) {
  const paymentPayload = buildPaymentPayload({
    bankName: formValues.bankName ?? "",
    routingNumber: formValues.routingNumber ?? "",
    accountNumber: formValues.accountNumber ?? "",
    validFrom: formValues.startDate,
    validTo: formValues.termDate,
    reviewContract: Boolean(formValues.reviewContract),
    clientManagementSplit: formValues.clientManagementSplit,
    providerSplit: formValues.providerSplit,
    marketingSplit: formValues.marketingSplit,
    billingAddress: null,
    mailingAddress: null,
  });

  return {
    entity: entityId,
    valid_from: paymentPayload.validFrom,
    valid_to: paymentPayload.validTo,
    bank_name: paymentPayload.bankName,
    account_number: paymentPayload.accountNumber,
    routing_number: paymentPayload.routingNumber,
    billing_address: paymentPayload.billingAddress,
    mailing_address: paymentPayload.mailingAddress,
    client_management_split: paymentPayload.clientManagementSplit,
    provider_split: paymentPayload.providerSplit,
    marketing_split: paymentPayload.marketingSplit,
    review_contract: paymentPayload.reviewContract,
  };
}

export function buildContractCreatePayload(formValues, entityId) {
  const contractPayload = buildPatchPayload(formValues);
  const payload = {
    entity: entityId,
    contract_name: contractPayload.contractName,
    start_date: contractPayload.startDate,
    employer_identification_number: contractPayload.employerIdentificationNumber,
    pip_state: contractPayload.pipState,
  };

  if (contractPayload.termDate) {
    payload.term_date = contractPayload.termDate;
  }

  return payload;
}

export function buildContractCreateWithPaymentPayload(formValues, entityId) {
  return {
    contract: buildContractCreatePayload(formValues, entityId),
    payment: buildContractPaymentPayload(formValues, entityId),
  };
}

export function buildContractCreateWithPaymentUploadPayload(formValues, entityId) {
  const payload = buildContractCreateWithPaymentPayload(formValues, entityId);
  const formData = new FormData();

  formData.append("contract", JSON.stringify(payload.contract));
  formData.append("payment", JSON.stringify(payload.payment));

  if (formValues.contractFile instanceof File) {
    formData.append("contract_file", formValues.contractFile);
  }

  return formData;
}

export const CONTRACT_PIP_OPTIONS = [
  { id: true, label: STRINGS.PIP_OPTS.true },
  { id: false, label: STRINGS.PIP_OPTS.false },
];
